import React from "react";
import { connect } from "react-redux";
import { Container, Row, Col, Card, CardBody, Badge as BadgeUI } from "reactstrap";
import api from "./../../../Api.js";
import SimpleHeader from "./../../../components/Headers/SimpleHeader.jsx";
import HpBar from "./HpBar.jsx";
import GoldDisplay from "./GoldDisplay.jsx";
import ReviveChallenge from "./ReviveChallenge.jsx";
import EnemyChallenge from "./EnemyChallenge.jsx";
import EventToast from "./EventToast.jsx";
import EventClaim from "./EventClaim.jsx";
import PixelCharacterFigure from "./../gamification/PixelCharacterFigure.jsx";

/**
 * LiveClassroom — tela do aluno na sessao ao vivo (regras 6, 7).
 * Mostra: personagem + HP + Gold + XP + recebe eventos via Socket.io.
 */
class LiveClassroom extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      status: null,
      session: null,
      enemy: null,
      event: null,
      claimEvent: null,
      eventClaimedCount: 0,
      equipped: [],
      classroomId: parseInt(this.getClassroomFromUrl())
    };
    this.pollInterval = null;
  }

  getClassroomFromUrl() {
    var match = window.location.pathname.match(/live\/(\d+)/);
    return match ? match[1] : null;
  }

  async componentDidMount() {
    await this.loadStatus();
    await this.loadEquipped();
    await this.checkSession();
    this.connectSocket();

    // Poll de fallback a cada 10s
    this.pollInterval = setInterval(() => {
      this.loadStatus();
      // rede de seguranca: se o personagem nao carregou, tenta de novo
      if (!this.state.equipped || !this.state.equipped.length) this.loadEquipped();
    }, 10000);
  }

  // usuario chegou depois do mount: recarrega tudo (senao ficava pelado/zerado)
  componentDidUpdate(prevProps) {
    var antes = prevProps.user && prevProps.user.id;
    var agora = this.props.user && this.props.user.id;
    if (!antes && agora) {
      this.loadStatus();
      this.loadEquipped();
      this.checkSession();
    }
  }

  componentWillUnmount() {
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.disconnectSocket();
  }

  // A arena pode montar antes do usuario chegar no redux (acesso direto pela
  // URL). Sem isso, o personagem ficava pelado e os dados zerados.
  getUserId() {
    if (this.props.user && this.props.user.id) return this.props.user.id;
    var sid = sessionStorage.getItem('userId');
    return sid ? parseInt(sid) : null;
  }

  async loadStatus() {
    var userId = this.getUserId();
    if (!userId) return;
    try {
      var res = await api.getRpgStatus(userId);
      this.setState({ status: res.data });
    } catch (e) {}
  }

  async loadEquipped() {
    var userId = this.getUserId();
    if (!userId) return;
    try {
      var res = await api.getUserEquipped(userId);
      this.setState({ equipped: res.data || [] });
    } catch (e) {}
  }

  async checkSession() {
    if (!this.state.classroomId) return;
    try {
      var res = await api.getActiveSession(this.state.classroomId);
      if (res.data && res.data.active) {
        this.setState({
          session: res.data.session,
          enemy: (res.data.enemies && res.data.enemies[0]) || null
        });
      }
    } catch (e) {}
  }

  connectSocket() {
    try {
      if (!window.socket) return;
      var self = this;
      var classroom = this.state.classroomId;

      window.socket.emit('join-classroom', { classroom: classroom });

      window.socket.on('rpg:enemy-spawned', function (enemy) {
        self.setState({ enemy: enemy });
      });

      window.socket.on('rpg:event-triggered', function (event) {
        if (event && (event.type === 'chest' || event.type === 'lightning_question')) {
          // Evento resgatavel — abre o modal interativo
          self.setState({ claimEvent: event, eventClaimedCount: 0 });
        } else {
          self.setState({ event: event });
          self.loadStatus(); // recarrega depois de receber recompensa
        }
      });

      window.socket.on('rpg:event-claimed', function (data) {
        var claimEvent = self.state.claimEvent;
        if (!claimEvent || !data) return;
        var eventId = data.eventId !== undefined ? data.eventId : data.id;
        if (eventId !== claimEvent.id) return;
        self.setState({
          eventClaimedCount: data.claimedCount !== undefined
            ? data.claimedCount
            : self.state.eventClaimedCount + 1
        });
      });

      window.socket.on('rpg:session-started', function () {
        self.checkSession();
      });

      window.socket.on('rpg:session-ended', function () {
        self.setState({ session: null, enemy: null, event: null, claimEvent: null, eventClaimedCount: 0 });
        self.checkSession();
      });

      window.socket.on('rpg:hp-lost', function () { self.loadStatus(); });
      window.socket.on('rpg:hp-gained', function () { self.loadStatus(); });
      window.socket.on('rpg:gold-gained', function () { self.loadStatus(); });
      window.socket.on('rpg:gold-lost', function () { self.loadStatus(); });
      window.socket.on('rpg:xp-gained', function () { self.loadStatus(); });

      window.socket.on('rpg:enemy-damaged', function (data) {
        if (data.defeated) {
          self.setState({ enemy: null });
          self.loadStatus();
        }
      });
    } catch (e) {}
  }

  disconnectSocket() {
    try {
      if (window.socket) {
        window.socket.off('rpg:enemy-spawned');
        window.socket.off('rpg:event-triggered');
        window.socket.off('rpg:event-claimed');
        window.socket.off('rpg:session-started');
        window.socket.off('rpg:session-ended');
        window.socket.off('rpg:hp-lost');
        window.socket.off('rpg:hp-gained');
        window.socket.off('rpg:gold-gained');
        window.socket.off('rpg:gold-lost');
        window.socket.off('rpg:xp-gained');
        window.socket.off('rpg:enemy-damaged');
      }
    } catch (e) {}
  }

  render() {
    var status = this.state.status || {};
    var hp = status.hp || { currentHp: 100, maxHp: 100 };
    var gold = status.gold || { balance: 0 };
    var xp = status.xp || { totalXp: 0, currentLevel: { level: 1, name: 'Bronze I' } };
    var died = hp.currentHp <= 0;

    return (
      <React.Fragment>
        <SimpleHeader name="Aula ao Vivo · RPG" way={[{ name: "RPG", url: "/live" }]} />

        <Container className="mt--6" fluid>
          <Row>
            {/* Personagem */}
            <Col xl="5" lg="6">
              <Card className="shadow text-center">
                <CardBody>
                  <div style={{ marginBottom: '20px' }}>
                    <PixelCharacterFigure
                      equipped={this.state.equipped}
                      scale={5}
                      direction={128}
                    />
                  </div>
                  <h3>{this.props.user ? this.props.user.name : 'Aventureiro'}</h3>
                  {this.state.session && (
                    <BadgeUI color="success" pill className="mb-3">
                      <i className="fas fa-broadcast-tower mr-1" />
                      Aula {this.state.session.classNumber} · AO VIVO
                    </BadgeUI>
                  )}
                  {!this.state.session && (
                    <BadgeUI color="secondary" pill className="mb-3">
                      Aguardando professor iniciar sessão...
                    </BadgeUI>
                  )}

                  {/* Status bars */}
                  <div className="mt-3">
                    <HpBar currentHp={hp.currentHp} maxHp={hp.maxHp} />
                  </div>
                  <Row className="mt-3 align-items-center">
                    <Col className="text-left">
                      <small className="text-muted d-block">Level {xp.currentLevel.level}</small>
                      <strong>{xp.currentLevel.name}</strong>
                      <small className="d-block text-muted">{xp.totalXp} XP</small>
                    </Col>
                    <Col className="text-right">
                      <GoldDisplay balance={gold.balance} />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Status info / Acoes */}
            <Col xl="7" lg="6">
              <Card className="shadow">
                <CardBody>
                  <h4>
                    <i className="fas fa-scroll mr-2 text-warning" />
                    Diário do Herói
                  </h4>
                  <p className="text-muted">
                    {this.state.enemy
                      ? '⚔️ Um inimigo apareceu! Prepare-se para o combate!'
                      : this.state.session
                        ? '✨ Aguardando próximo desafio... fique atento!'
                        : '🪧 Aguarde o professor iniciar a sessão de aula.'}
                  </p>

                  <div className="mt-4">
                    <h5>Suas habilidades</h5>
                    {status.skills && status.skills.length > 0 ? (
                      <div>
                        {status.skills.map((s) => (
                          <BadgeUI key={s.skill} color="info" pill className="mr-2 mb-2" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                            <i className={(s.icon || 'fas fa-magic') + ' mr-1'} />
                            {s.name}
                          </BadgeUI>
                        ))}
                      </div>
                    ) : (
                      <small className="text-muted">
                        Suba para o nível 2 para escolher uma habilidade na <a href="/shop/skills">loja</a>.
                      </small>
                    )}
                  </div>

                  <div className="mt-4">
                    <h5>Atalhos</h5>
                    <a href="/shop" className="btn btn-warning btn-sm mr-2">
                      <i className="fas fa-store mr-1" /> Lojinha
                    </a>
                    <a href="/character-rpg" className="btn btn-info btn-sm mr-2">
                      <i className="fas fa-user-cog mr-1" /> Personagem
                    </a>
                    <a href="/achievements" className="btn btn-success btn-sm">
                      <i className="fas fa-trophy mr-1" /> Conquistas
                    </a>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal de combate */}
        {this.state.enemy && !died && (
          <EnemyChallenge
            enemy={this.state.enemy}
            heroEquipped={this.state.equipped}
            onDefeated={() => {
              this.setState({ enemy: null });
              this.loadStatus();
            }}
          />
        )}

        {/* Modal de revive (HP zerou) */}
        {died && (
          <ReviveChallenge
            userId={this.props.user.id}
            classroom={this.state.classroomId}
            onRevived={() => this.loadStatus()}
          />
        )}

        {/* Modal de evento resgatavel (bau / pergunta relampago) */}
        {this.state.claimEvent && (
          <EventClaim
            event={this.state.claimEvent}
            userId={this.props.user ? this.props.user.id : null}
            claimedCount={this.state.eventClaimedCount}
            onClose={() => {
              this.setState({ claimEvent: null, eventClaimedCount: 0 });
              this.loadStatus();
            }}
            onClaimed={() => {
              this.setState({ claimEvent: null, eventClaimedCount: 0 });
              this.loadStatus();
            }}
          />
        )}

        {/* Toast de evento aleatório (eventos passivos) */}
        {this.state.event && (
          <EventToast
            event={this.state.event}
            onClose={() => this.setState({ event: null })}
          />
        )}
      </React.Fragment>
    );
  }
}

export default connect(state => ({
  user: state.user,
  translate: state.translate
}))(LiveClassroom);
